import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Megaphone, Pause, Play } from '../../../../icons';
import type { NoticeSlideDto } from '../../../../api/notice-slides';
import { WidgetCard } from '../WidgetCard';
import { useWidgetActions } from '../widget-context';
import { useNoticeSlides } from '../widget-queries';

/**
 * 공지 슬라이드 위젯 (migration 172 · 시안 화면 E · 정본 제안 D16).
 *
 * ★★★ **그림 공지와 글 공지가 한 줄에서 돈다.** 둘을 다른 위젯으로 가르면 회사는 같은 공지를
 *     두 곳에 올려야 하고, 사람은 두 곳을 봐야 한다. 그림이 없는 공지는 «글자만으로 한 장» 이
 *     되고 태그 색으로 갈린다 — 빈 그림을 지어내지 않는다.
 *
 * ★★★ **손이 올라오면 멈춘다.** 읽는 중에 넘어가는 위젯은 다음부터 안 읽힌다.
 *
 * ★ 「움직임 줄이기」를 켠 사람에게는 밀지 않고 겹쳐 넘긴다(설정값과 무관).
 *
 * ⚠ 기존 `notices`(공지 목록) 위젯은 그대로 둔다 — 목록으로 보고 싶은 회사가 있다.
 */
export function NoticeSlideWidget() {
  const { go } = useWidgetActions();
  const { data, loading, error } = useNoticeSlides();
  const slides = useMemo(() => data?.slides ?? [], [data]);
  const set = data?.set ?? null;

  const [at, setAt] = useState(0);
  const [paused, setPaused] = useState(false);
  const [manualStop, setManualStop] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const reduceMotion = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const count = slides.length;
  // 목록이 줄어도 자리가 남지 않게 — 슬라이드를 지우면 곧바로 첫 장으로 돌아온다.
  useEffect(() => { setAt((i) => (count === 0 ? 0 : i % count)); }, [count]);

  const step = useCallback((d: number) => {
    setAt((i) => (count === 0 ? 0 : ((i + d) % count + count) % count));
  }, [count]);

  const autoOn = Boolean(set?.autoPlay) && !manualStop && !paused && count > 1;
  const intervalMs = Math.max(4, set?.intervalSec ?? 6) * 1000;

  useEffect(() => {
    if (!autoOn) return undefined;
    const t = window.setInterval(() => step(1), intervalMs);
    return () => window.clearInterval(t);
  }, [autoOn, intervalMs, step]);

  const cur = slides[at];
  const onImage = cur?.kind === 'IMAGE';
  // 밀기는 그림이 섞여 있어야 뜻이 있다. 「움직임 줄이기」면 항상 겹쳐 넘긴다.
  const anim = reduceMotion ? 'fade' : (set?.transition === 'FADE' ? 'fade' : 'slide');

  const open = (s: NoticeSlideDto) => {
    if (!s.linkUrl) return;
    if (/^https?:\/\//i.test(s.linkUrl)) window.open(s.linkUrl, '_blank', 'noopener');
    else go(s.linkUrl);
  };

  return (
    <WidgetCard
      title="공지사항"
      icon={Megaphone}
      loading={loading}
      error={error}
      empty={!loading && !error && count === 0 ? '보여줄 공지가 없습니다' : null}
      flush
    >
      <div
        ref={boxRef}
        className={`ns${onImage ? ' ns--onimage' : ''}${paused ? ' is-paused' : ''}`}
        data-anim={anim}
        data-auto={autoOn ? 'on' : 'off'}
        role="group"
        aria-roledescription="슬라이드 쇼"
        aria-label="사내 공지"
        onPointerEnter={() => setPaused(true)}
        onPointerLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(e) => {
          if (!boxRef.current?.contains(e.relatedTarget as Node | null)) setPaused(false);
        }}
      >
        <div
          className="ns__track"
          style={anim === 'slide' ? { transform: `translateX(${-at * 100}%)` } : undefined}
        >
          {slides.map((s, i) => {
            const on = i === at;
            const clickable = Boolean(s.linkUrl);
            return (
              <article
                key={s.id}
                className={`ns__slide ns__slide--${s.kind === 'IMAGE' ? 'img' : 'text'}${on ? ' is-on' : ''}`}
                aria-hidden={!on}
                {...(clickable
                  ? {
                    role: 'link', tabIndex: on ? 0 : -1,
                    onClick: () => open(s),
                    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') open(s); },
                  }
                  : {})}
                data-clickable={clickable || undefined}
              >
                {s.kind === 'IMAGE' && s.imageUrl && (
                  <>
                    <img className="ns__bg" src={s.imageUrl} alt="" aria-hidden />
                    {s.scrim && <span className="ns__scrim" aria-hidden />}
                  </>
                )}
                <div className={`ns__body ns__body--${s.textPos.toLowerCase()}`}>
                  {s.tagLabel && (
                    <span className="ns__tag" style={s.tagColor ? { ['--c' as string]: s.tagColor } : undefined}>
                      {s.tagLabel}
                    </span>
                  )}
                  <h3 className="ns__t">{s.title}</h3>
                  {s.summary && <p className="ns__s">{s.summary}</p>}
                </div>
              </article>
            );
          })}
        </div>

        {count > 1 && (
          <>
            <button
              type="button" className="ns__arrow ns__arrow--prev"
              aria-label="이전 공지" onClick={() => step(-1)}
            >
              <ChevronLeft size={14} aria-hidden />
            </button>
            <button
              type="button" className="ns__arrow ns__arrow--next"
              aria-label="다음 공지" onClick={() => step(1)}
            >
              <ChevronRight size={14} aria-hidden />
            </button>
          </>
        )}

        <div className="ns__bar">
          {count > 1 && set?.autoPlay && (
            <button
              type="button" className="ns__play"
              aria-label={manualStop ? '자동 넘김 시작' : '자동 넘김 멈춤'}
              onClick={() => setManualStop((v) => !v)}
            >
              {manualStop ? <Play size={12} aria-hidden /> : <Pause size={12} aria-hidden />}
            </button>
          )}
          {count > 1 && (
            <span className="ns__dots" role="tablist" aria-label="공지 고르기">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={i === at}
                  aria-label={`${i + 1}번째 공지`}
                  className={`ns__dot${i === at ? ' is-on' : ''}`}
                  onClick={() => setAt(i)}
                >
                  {/* 점 안에서 남은 시간이 찬다 — 언제 넘어갈지가 보여야 사람이 기다린다.
                      ★ 채우개를 «켜진 점에만» 두고 key 를 at 으로 잡는다. 클래스만 갈아 끼우면
                        React 가 같은 DOM 을 재사용해 애니메이션이 처음으로 안 돌아간다. */}
                  {i === at && autoOn && (
                    <i key={at} className="ns__fill" style={{ ['--ns-dur' as string]: `${intervalMs}ms` }} aria-hidden />
                  )}
                </button>
              ))}
            </span>
          )}
          {count > 0 && <span className="ns__count">{at + 1} / {count}</span>}
        </div>
      </div>
    </WidgetCard>
  );
}
